import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { institutionApi } from '../api/institution.api'
import type { ReportFilters } from '../types'

export const INSTITUTION_KEY = ['institution'] as const
export const STUDENTS_KEY = ['institution', 'students'] as const
export const CLASSES_KEY = ['institution', 'classes'] as const
export const SHARES_KEY = ['institution', 'courses'] as const
export const REPORT_KEY = ['institution', 'report'] as const

export function useInstitution() {
  return useQuery({
    queryKey: INSTITUTION_KEY,
    queryFn: () => institutionApi.fetchInstitution().then((r) => r.data.data),
    // Un compte sans établissement reçoit un 403 définitif : réessayer est inutile.
    retry: false,
  })
}

export function useInstitutionStudents() {
  return useQuery({
    queryKey: STUDENTS_KEY,
    queryFn: () => institutionApi.fetchStudents().then((r) => r.data.data),
  })
}

export function useInstitutionClasses() {
  return useQuery({
    queryKey: CLASSES_KEY,
    queryFn: () => institutionApi.fetchClasses().then((r) => r.data.data),
  })
}

export function useSharedCourses() {
  return useQuery({
    queryKey: SHARES_KEY,
    queryFn: () => institutionApi.fetchSharedCourses().then((r) => r.data.data),
  })
}

export function useProgressReport(filters: ReportFilters) {
  return useQuery({
    queryKey: [...REPORT_KEY, filters],
    queryFn: () => institutionApi.fetchProgressReport(filters).then((r) => r.data.data),
  })
}

/** Un import change à la fois les effectifs, les classes et les compteurs. */
export function useImportStudents() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: File) => institutionApi.importStudents(file).then((r) => r.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INSTITUTION_KEY })
      void queryClient.invalidateQueries({ queryKey: STUDENTS_KEY })
      void queryClient.invalidateQueries({ queryKey: CLASSES_KEY })
    },
  })
}

export function useDisableStudent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => institutionApi.disableStudent(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STUDENTS_KEY })
      void queryClient.invalidateQueries({ queryKey: INSTITUTION_KEY })
    },
  })
}

export function useCreateClass() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { name: string; level?: string }) => institutionApi.createClass(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CLASSES_KEY })
      void queryClient.invalidateQueries({ queryKey: INSTITUTION_KEY })
    },
  })
}

export function useAssignStudent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ classId, userId }: { classId: string; userId: string }) =>
      institutionApi.assignStudent(classId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CLASSES_KEY }),
  })
}

export function useShareCourse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { course_id: string; class_id?: string | null }) =>
      institutionApi.shareCourse(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SHARES_KEY }),
  })
}

export function useRevokeShare() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ courseId, classId }: { courseId: string; classId?: string }) =>
      institutionApi.revokeShare(courseId, classId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SHARES_KEY }),
  })
}
